import React, { useState, useEffect } from "react";
import { inject, observer } from "mobx-react";
import QRCode from "qrcode";
import { Copy, Check, Plus, Trash2, Loader2, X, CheckCircle2 } from "lucide-react";
import Modal from "./Modal";
import { showToast } from "../Utils/toast";

// QR + link the DJ shares so guests submit YouTube links into the live queue.
// Links never auto-expire (revoke manually). The right panel lists pending /
// failed submissions aggregated across all of the DJ's active links.
function DjShareModal({ isOpen, onClose, DjStore }) {
  const token = DjStore.submissionCurrentToken;
  const currentLink = DjStore.submissionCurrentLink;
  const submitLink = token ? `${window.location.origin}/submit/${token}` : "";
  const [qrDataURL, setQrDataURL] = useState(null);
  const [copying, setCopying] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      DjStore.ensureSubmissionLink().catch(() =>
        showToast.error("Could not start a submission link")
      );
    }
  }, [isOpen, DjStore]);

  useEffect(() => {
    if (!submitLink) {
      setQrDataURL(null);
      return;
    }
    QRCode.toDataURL(submitLink, {
      width: 256,
      margin: 2,
      color: { dark: "#ffffff", light: "#0a0a0a" },
    })
      .then(setQrDataURL)
      .catch(() => showToast.error("Failed to generate QR code"));
  }, [submitLink]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(submitLink);
      setCopying(true);
      showToast.success("Link copied!");
      setTimeout(() => setCopying(false), 2000);
    } catch {
      showToast.error("Failed to copy link");
    }
  };

  const newLink = async () => {
    setBusy(true);
    try {
      await DjStore.createSubmissionLink();
    } catch {
      showToast.error("Could not create a link");
    } finally {
      setBusy(false);
    }
  };

  const pending = DjStore.submissionPending;
  const links = DjStore.submissionLinks;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Crowd Submit" size="xl">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left: current link + controls + active-link list */}
        <div className="md:w-[300px] shrink-0 space-y-4">
          <div className="flex items-center justify-center gap-3">
            <label className="text-sm font-medium text-text">
              Links per guest
            </label>
            <input
              type="number"
              min={1}
              max={20}
              value={DjStore.submissionMaxLinks}
              onChange={(e) => DjStore.setSubmissionMaxLinks(e.target.value)}
              className="w-20 px-2 py-1 bg-secondary border border-neutral-700 rounded text-center text-text focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>

          <div className="flex justify-center">
            {qrDataURL ? (
              <div className="relative p-4 bg-primary border border-neutral-700 rounded-lg">
                <img src={qrDataURL} alt="Submit QR Code" className="w-56 h-56" />
                {currentLink?.scanned && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/75 rounded-lg">
                    <CheckCircle2 className="text-emerald-400" size={64} />
                    <span className="text-emerald-300 font-semibold">Scanned</span>
                    <span className="text-[11px] text-neutral-300 text-center px-6">
                      Still works — generate a new one if you want a fresh link.
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-56 h-56 flex items-center justify-center bg-secondary rounded-lg border border-neutral-700">
                <p className="text-sm text-gray-400">No active link</p>
              </div>
            )}
          </div>

          {submitLink && (
            <div className="flex gap-2">
              <input
                type="text"
                value={submitLink}
                readOnly
                className="flex-1 min-w-0 px-3 py-2 bg-secondary border border-neutral-700 rounded-lg text-text text-xs focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <button
                onClick={copyLink}
                className="px-3 py-2 bg-accent text-white rounded-lg hover:opacity-90 transition-opacity"
                aria-label="Copy submit link"
              >
                {copying ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          )}

          <button
            onClick={newLink}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg font-medium bg-neutral-700 text-text hover:bg-neutral-600 transition-colors disabled:opacity-50"
          >
            <Plus size={16} /> New link
          </button>

          {/* Active links */}
          {links.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-wider text-text-muted">
                Active links ({links.length})
              </div>
              {links.map((l) => {
                const active = l.token === token;
                return (
                  <div
                    key={l.token}
                    onClick={() => DjStore.selectSubmissionLink(l.token)}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${active ? "bg-neutral-800 ring-1 ring-accent" : "hover:bg-neutral-900"}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${l.scanned ? "bg-emerald-400" : "bg-neutral-600"}`}
                      title={l.scanned ? "Scanned" : "Not scanned yet"}
                    />
                    <span className="font-mono text-text truncate flex-1">
                      …{l.token.slice(-5)}
                    </span>
                    <span className="text-text-muted">{l.maxLinks}/guest</span>
                    {l.pendingCount > 0 && (
                      <span className="text-amber-400">{l.pendingCount}⏳</span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        DjStore.revokeSubmissionLink(l.token);
                      }}
                      className="text-text-muted hover:text-red-400"
                      title="Revoke this link"
                      aria-label="Revoke link"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: pending / failed submissions across all links */}
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-text-muted mb-2">
            Incoming ({pending.length})
          </div>
          {pending.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-sm text-gray-500">
              No pending submissions
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[22rem] overflow-y-auto thin-scroll pr-1">
              {pending.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 p-2 rounded-lg bg-neutral-900 border border-neutral-800"
                >
                  <span className={s.status === "error" ? "text-red-400" : "text-amber-400"}>
                    {s.status === "error" ? <X size={16} /> : <Loader2 size={16} className="animate-spin" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="m-0 text-sm text-white truncate">
                      {s.title || s.url}
                    </p>
                    {s.status === "error" && (
                      <p className="m-0 text-xs text-red-400 truncate">
                        {s.error || "Failed"}
                      </p>
                    )}
                  </div>
                  <span className="text-[11px] text-neutral-400 shrink-0">
                    {s.status === "error" ? "Failed" : "Downloading"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default inject("DjStore")(observer(DjShareModal));
